import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/shared/Header";
import LandingLiveRecording from "@/components/landing/LandingLiveRecording";
import { LandingAudioProcessingCard } from "@/components/shared/LandingAudioProcessingCard";
import { LandingChatDemo } from "@/components/shared/LandingChatDemo";
import { LandingSpeakersAnnouncement } from "@/components/shared/LandingSpeakersAnnouncement";
import { LandingPromoVideoCard, LandingPromoVideoDialog } from "@/components/shared/LandingPromoVideoCard";
import { SiteFooter } from "@/components/shared/SiteFooter";
import { AUTH_LOGO_INLINE } from "@/lib/authLogoInline";
import { useLanguage } from "@/context/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Upload,
  Headphones,
  FileText,
  Target,
  MessageSquareMore,
  Zap,
  Globe,
  Search,
  Share2,
  Check,
  X,
} from "lucide-react";

const LandingPage = () => {
  const [isLandingVideoOpen, setIsLandingVideoOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const landingPromoVideoSrc = "/voxara-landing-demo.mp4";
  const { t, language } = useLanguage();
  const heroTitle2 = t("heroTitle2");
  const heroTitle3 = t("heroTitle3");
  const heroTitle2Words = heroTitle2.split(" ");
  const heroLead = heroTitle2Words.slice(0, 2).join(" ") || heroTitle2;
  const heroAccent = heroTitle2Words.slice(2).join(" ") || "";
  const heroTitle3Match = heroTitle3.match(/^(.*?)(Voxara\.?)+$/i);
  const heroTitle3Prefix = heroTitle3Match?.[1]?.trim() || heroTitle3;
  const heroTitle3Brand = heroTitle3Match?.[2] || "";
  const heroTitle3PrefixCore = language === "es"
    ? heroTitle3Prefix.replace(/\s+de$/i, "").trim()
    : heroTitle3Prefix;
  const heroDescriptionIntro = t("heroDescriptionIntro");
  const heroDescriptionHighlight = t("heroDescriptionHighlight");
  const heroDescriptionOutro = t("heroDescriptionOutro");

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);
    syncViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
    } else {
      mediaQuery.addListener(syncViewport);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", syncViewport);
      } else {
        mediaQuery.removeListener(syncViewport);
      }
    };
  }, []);

  // Demo key moments - translated based on language
  const demoMoments = language === "es" ? [
    {
      time: "02:14",
      text: "Los ingresos del Q3 revelan un crecimiento del 40% — cambio crítico en la trayectoria.",
    },
    {
      time: "07:48",
      text: "Nuevo roadmap de producto anunciado — se introduce pivote estratégico.",
    },
    {
      time: "15:30",
      text: "Sesión de preguntas desafiante sobre rotación — surge preocupación sin resolver.",
    },
  ] : [
    {
      time: "02:14",
      text: "Q3 revenue reveals 40% growth — critical shift in trajectory.",
    },
    {
      time: "07:48",
      text: "New product roadmap announced — strategic pivot introduced.",
    },
    {
      time: "15:30",
      text: "Challenging Q&A on churn — unresolved concern surfaces.",
    },
  ];

  const demoQuote = language === "es" 
    ? { highlight: "resultados trimestrales", text: "superaron las expectativas — derivado de nuestro enfoque en retención sobre adquisición pura..." }
    : { highlight: "quarterly results", text: "exceeded expectations — stemming from our focus on retention over pure acquisition..." };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <Header transparent />
      <LandingSpeakersAnnouncement language={language} />

      {/* Hero Section */}
      <section className="relative px-6 pb-16 pt-24 sm:pt-28 md:pt-28 lg:pt-32 xl:pt-48">
        <div className="container mx-auto max-w-6xl">
          <div className="grid items-start gap-10 xl:grid-cols-[minmax(0,34rem)_minmax(0,1fr)] xl:gap-12">
            {/* Left Content */}
            <div className="animate-fade-in hero-copy-panel mx-auto w-full max-w-[22rem] text-center sm:max-w-[24rem] md:max-w-[22rem] md:text-center lg:max-w-[24rem] xl:mx-0 xl:max-w-[34rem] xl:text-left" style={{ animationDelay: "0.1s" }}>
              <div className="hero-copy-glow"></div>
              {/* AI Video Intelligence Badge - Above heading */}
              <span className="inline-block px-4 py-1.5 text-xs font-mono uppercase tracking-wider text-primary border border-primary/30 rounded-full mb-6" data-testid="hero-badge">
                {t("aiVideoIntelligence")}
              </span>
              <h1
                className="font-bold tracking-tight leading-[0.98] mb-6 text-balance max-w-[28ch] mx-auto xl:mx-0"
                data-testid="landing-hero-heading"
              >
                <span className="mx-auto block max-w-[16ch] text-pretty text-3xl leading-[1.06] tracking-tight text-foreground sm:text-[2.7rem] md:max-w-[14ch] md:text-[2.25rem] lg:max-w-[15ch] lg:text-[2.45rem] xl:mx-0 xl:max-w-[18ch] xl:text-[3.2rem]">
                  {t("heroTitle1")}
                </span>

                <span className="mx-auto mt-6 flex max-w-[16ch] flex-col items-center text-center text-pretty md:max-w-[14ch] lg:max-w-[15ch] xl:mx-0 xl:max-w-[20ch] xl:items-start xl:text-left">
                  <span className="block text-3xl leading-[1.02] tracking-tight text-foreground sm:text-[2.45rem] md:text-[1.95rem] lg:text-[2.05rem] xl:text-[3.1rem]">
                    {heroLead}
                  </span>
                  {heroAccent ? (
                    <span className="relative mt-1 block w-fit text-center text-[2rem] leading-[0.96] tracking-[-0.05em] accent-text drop-shadow-[0_0_18px_rgba(248,0,248,0.16)] sm:text-[2.55rem] sm:tracking-tight md:text-[1.95rem] md:tracking-[-0.07em] lg:text-[2.05rem] lg:tracking-[-0.06em] xl:mt-0 xl:w-auto xl:text-left xl:text-[3.1rem] xl:tracking-tight">
                      {heroAccent}
                    </span>
                  ) : null}
                  <span className="mx-auto mt-1 block max-w-[10ch] text-balance text-center text-[1.55rem] leading-[0.98] tracking-tight text-foreground sm:max-w-none sm:text-[1.5rem] md:text-[1.35rem] lg:text-[1.5rem] xl:mx-0 xl:mt-0 xl:w-max xl:max-w-none xl:text-left xl:text-[1.9rem]">
                    {language === "es" && heroTitle3Brand ? (
                      <>
                        <span className="block xl:whitespace-nowrap">{heroTitle3PrefixCore}</span>
                        <span className="block">
                          de <span className="accent-text">{heroTitle3Brand}</span>
                        </span>
                      </>
                    ) : (
                      <>
                        {heroTitle3Prefix}
                        {heroTitle3Brand ? <span className="accent-text"> {heroTitle3Brand}</span> : null}
                      </>
                    )}
                  </span>
                </span>
              </h1>
              <p
                className="mx-auto mb-8 max-w-[19rem] text-base leading-relaxed text-muted-foreground text-pretty sm:max-w-xl md:max-w-[22rem] md:text-[0.95rem] lg:max-w-[24rem] lg:text-[1rem] xl:mx-0 xl:max-w-2xl xl:text-lg"
                data-testid="landing-hero-description"
              >
                <span className="block">{heroDescriptionIntro}</span>
                <span className="hero-highlight-inline mt-3 inline-block" data-testid="hero-description-highlight">
                  {heroDescriptionHighlight}
                </span>
                <span className="mt-3 block">{heroDescriptionOutro}</span>
              </p>
              <div className="mb-6 flex w-full flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 xl:justify-start">
                <Link to="/signup">
                  <Button
                    size="lg"
                    className="btn-glow min-h-12 w-full max-w-full whitespace-normal px-4 py-3 text-center text-sm font-semibold leading-5 sm:w-auto sm:px-5 sm:text-base bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
                    data-testid="hero-upload-btn"
                  >
                    <Upload className="w-5 h-5" />
                    {language === "es" ? "Transcribe tu video/audio/texto" : "Transcribe your video/audio/text"}
                  </Button>
                </Link>

                <Button
                  type="button"
                  variant="outline"
                  className="hidden md:inline-flex gap-2 border-border/60 bg-card/70 text-foreground hover:bg-card"
                  onClick={() => setIsLandingVideoOpen(true)}
                  data-testid="landing-open-video-modal-button"
                >
                  {language === "es" ? "Ver video" : "Watch video"}
                </Button>
              </div>
              <p className="mb-2 text-sm text-muted-foreground mx-auto xl:mx-0" data-testid="hero-free-trial-copy">
                {language === "es"
                  ? "Cree una cuenta y pruebe hasta 30 minutos gratis."
                  : "Create an account and try up to 30 minutes free."}
              </p>
            </div>

            {/* Right - Mobile video / Desktop audio processing */}
            <div className="animate-fade-in xl:mx-0 xl:w-auto xl:mt-5" style={{ animationDelay: "0.2s" }}>
              {isMobileViewport ? (
                <div className="md:hidden" data-testid="landing-mobile-video-slot">
                  <LandingPromoVideoCard language={language} videoSrc={landingPromoVideoSrc} />
                </div>
              ) : null}
              <div className="hidden xl:block" data-testid="landing-desktop-audio-processing-slot">
                <LandingAudioProcessingCard
                  t={t}
                  language={language}
                  demoQuote={demoQuote}
                  demoMoments={demoMoments}
                />
              </div>
            </div>
          </div>
        </div>

        <LandingPromoVideoDialog
          language={language}
          videoSrc={landingPromoVideoSrc}
          open={isLandingVideoOpen}
          onOpenChange={setIsLandingVideoOpen}
        />
      </section>

      {/* Divider */}
      <div className="container mx-auto px-6">
        <div className="border-t border-border" />
      </div>

      {/* How it Works */}
      <section id="how" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("howVoxaraWorks").split("Voxara")[0]}
              <span className="accent-text">Voxara</span>
              {t("howVoxaraWorks").split("Voxara")[1]}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: t("step01"),
                icon: Upload,
                title: t("uploadVideo"),
                desc: t("uploadVideoDesc"),
              },
              {
                step: t("step02"),
                icon: Headphones,
                title: t("audioExtraction"),
                desc: t("audioExtractionDesc"),
              },
              {
                step: t("step03"),
                icon: FileText,
                title: t("aiTranscription"),
                desc: t("aiTranscriptionDesc"),
              },
              {
                step: t("step04"),
                icon: Target,
                title: t("keyMomentAnalysis"),
                desc: t("keyMomentAnalysisDesc"),
              },
            ].map((item, idx) => (
              <React.Fragment key={idx}>
                <Card
                  className="bg-card border-border p-6 card-hover"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  data-testid={`landing-how-step-card-${idx + 1}`}
                >
                  <span className="text-xs font-mono text-primary uppercase tracking-wider">
                    {item.step}
                  </span>
                  <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center my-4">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </Card>

                {idx === 3 ? (
                  <div className="xl:hidden md:col-span-2 lg:col-span-4 flex justify-center" data-testid="landing-mobile-audio-processing-slot">
                    <LandingAudioProcessingCard
                      t={t}
                      language={language}
                      demoQuote={demoQuote}
                      demoMoments={demoMoments}
                      className="w-full max-w-2xl text-left"
                    />
                  </div>
                ) : null}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <Card
              className="w-full max-w-2xl bg-card border-border p-6 text-center card-hover"
              style={{ animationDelay: "0.45s" }}
              data-testid="landing-how-step-05-card"
            >
              <span className="text-xs font-mono text-primary uppercase tracking-wider" data-testid="landing-how-step-05-label">
                {t("step05")}
              </span>
              <div className="mx-auto my-4 flex h-12 w-12 items-center justify-center rounded bg-primary/10">
                <MessageSquareMore className="h-6 w-6 text-primary" />
              </div>
              <div className="mb-2 flex flex-col items-center gap-3">
                <Badge variant="secondary" className="rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.2em]" data-testid="landing-how-step-05-tier-badge">
                  {language === "es" ? "Solo Professional & Enterprise" : "Professional & Enterprise Only"}
                </Badge>
                <h3 className="text-lg font-semibold" data-testid="landing-how-step-05-title">{t("talkToYourContent")}</h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground" data-testid="landing-how-step-05-description">
                {t("talkToYourContentDesc")}
              </p>
            </Card>
          </div>

          <div className="mt-6 flex justify-center">
            <LandingChatDemo />
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-6">
        <div className="border-t border-border" />
      </div>

      <LandingLiveRecording />

      {/* Divider */}
      <div className="container mx-auto px-6">
        <div className="border-t border-border" />
      </div>

      {/* Features */}
      <section id="features" className="py-20 px-6 bg-card/30">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("builtForTitle1")}{" "}
              <span className="accent-text italic">{t("builtForTitle2")}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                tag: t("intelligence"),
                icon: Target,
                title: t("contextualKeyMoments"),
                desc: t("contextualKeyMomentsDesc"),
              },
              {
                tag: t("performance"),
                icon: Zap,
                title: t("audioFirstPipeline"),
                desc: t("audioFirstPipelineDesc"),
              },
              {
                tag: t("transcription"),
                icon: FileText,
                title: t("fullSearchableTranscripts"),
                desc: t("fullSearchableTranscriptsDesc"),
              },
              {
                tag: t("languages"),
                icon: Globe,
                title: t("fortyPlusLanguages"),
                desc: t("fortyPlusLanguagesDesc"),
              },
              {
                tag: t("library"),
                icon: Search,
                title: t("videoHistorySearch"),
                desc: t("videoHistorySearchDesc"),
              },
              {
                tag: t("collaboration"),
                icon: Share2,
                title: t("shareExport"),
                desc: t("shareExportDesc"),
              },
            ].map((item, idx) => (
              <Card key={idx} className="bg-card border-border p-6 card-hover">
                <span className="inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-primary/10 text-primary rounded mb-4">
                  {item.tag}
                </span>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.desc}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-6 border-y border-border">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              { value: "98.4%", label: t("accuracy") },
              { value: "40+", label: t("languages") },
              { value: "<60s", label: t("processing") },
            ].map((stat, idx) => (
              <div key={idx}>
                <div className="text-3xl md:text-4xl font-bold accent-text mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
              {t("pricingTitle1")}{" "}
              <span className="accent-text italic">{t("pricingTitle2")}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                name: t("starter"),
                price: "29.99",
                features: [
                  { text: t("starterDesc1"), included: true },
                  { text: t("starterDesc2"), included: true },
                  { text: t("starterDesc3"), included: true },
                  { text: t("starterDesc4"), included: true },
                  { text: t("starterDesc5"), included: true },
                  { text: t("starterDesc6"), included: true },
                  { text: t("starterDesc7"), included: true },
                  { text: t("starterDesc8"), included: true },
                ],
              },
              {
                name: t("professional"),
                price: "39.99",
                featured: true,
                features: [
                  { text: t("fifteenHoursDay"), included: true },
                  { text: t("fullTranscription"), included: true },
                  { text: t("keyMomentDetection"), included: true },
                  { text: t("proDesc1"), included: true },
                  { text: t("proDesc2"), included: true },
                  { text: t("proDesc3"), included: true },
                  { text: t("proDesc4"), included: true },
                ],
              },
              {
                name: t("enterprise"),
                price: "199.00",
                perUser: true,
                features: [
                  { text: t("enterpriseDesc1"), included: true },
                  { text: t("enterpriseDesc2"), included: true },
                  { text: t("enterpriseDesc3"), included: true },
                  { text: t("enterpriseDesc4"), included: true },
                  { text: t("enterpriseDesc5"), included: true },
                  { text: t("enterpriseDesc6"), included: true },
                  { text: t("enterpriseDesc7"), included: true },
                  { text: t("enterpriseDesc9"), included: true },
                ],
              },
            ].map((plan, idx) => (
              <Card
                key={idx}
                className={`p-6 relative flex flex-col ${
                  plan.featured
                    ? "bg-card border-primary shadow-[0_0_30px_rgba(248,0,248,0.2)]"
                    : "bg-card border-border"
                }`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-mono bg-primary text-primary-foreground rounded-full">
                    {t("mostPopular")}
                  </span>
                )}
                <div className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4">
                  {plan.name}
                </div>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-sm text-muted-foreground">$</span>
                  <span className="text-5xl font-bold">{plan.price}</span>
                </div>
                <div className="text-xs text-muted-foreground mb-6">
                  {plan.perUser ? t("perUserMonth") : t("perMonth")}
                </div>
                <div className="border-t border-border pt-6 mb-6 flex-grow">
                  <ul className="space-y-3">
                    {plan.features.map((feature, fidx) => (
                      <li
                        key={fidx}
                        className={`flex items-center gap-2 text-sm ${
                          feature.included
                            ? "text-foreground"
                            : "text-muted-foreground/50"
                        }`}
                      >
                        {feature.included ? (
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 flex-shrink-0" />
                        )}
                        {feature.text}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/signup" className="mt-auto">
                  <Button
                    className={`w-full ${
                      plan.featured
                        ? "bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
                        : "bg-muted text-foreground hover:bg-muted/80"
                    }`}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  >
                    {plan.featured ? t("startFreeTrial") : t("getStarted")}
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default LandingPage;
